/**
 * First birthday decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const firstBirthdayDecorationsPack: ToilePack = {
    slug: 'first-birthday-decorations',
    category: 'occasions',
    name: 'First birthday decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'theme', label: 'Theme', subject: 'a jungle-themed party wall with leaves and animal cutouts'},
        {key: 'cakesmash', label: 'Cake smash', subject: 'a cake smash setup with balloons'},
        {key: 'number', label: 'Number', subject: 'a giant number one balloon'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'soft pastel decoration with balloon garlands and paper fans, gentle daylight from camera-left, an airy light balance with soft shadows', palette: 'peach, mint, lilac and cream', framing: 'eye-level view, the whole setup in frame'},
        {key: 'bright', label: 'Bright', style: 'bright colourful decoration with balloons, paper pom-poms and streamers, cheerful daylight from overhead, a vivid balance with short shadows', palette: 'red, yellow, blue and green', framing: 'straight-on view, symmetric, centered'},
        {key: 'boho', label: 'Boho', style: 'boho decoration with dried pampas, rattan and neutral balloons, warm afternoon light from camera-right, a soft warm balance with gentle shadows', palette: 'beige, terracotta, sage and white', framing: 'three-quarter view with some of the room around it'},
    ],
    directions: ['car theme', 'princess theme', 'space theme'],
    directionHint: 'try: car theme',
    seo: {
        title: 'First birthday decoration: theme, cake smash, number',
        metaDescription: 'Nine first birthday decoration pictures: a theme wall, a cake smash setup and a giant number one balloon, in pastel, bright and boho looks.',
        h1: 'First birthday decoration: theme wall, cake smash, number balloon',
        tagline: 'Nine pictures, three first-birthday pieces, each in a pastel, a bright and a boho look.',
        intro: "First birthday decoration for a one-year-old's party at home: a jungle-themed party wall with leaves and animal cutouts, a cake smash setup with balloons, and a giant number one balloon. Each piece appears in a soft pastel look of balloon garlands and paper fans, a bright look of balloons, pom-poms and streamers, and a boho look of dried pampas and neutral balloons. Type a twist, such as car theme or space theme, and the same pieces carry it.",
        useCases: [
            'a theme wall for a first birthday',
            'a cake smash corner for photos',
            'a bright party setup for a large family',
            'a boho setup for a small gathering',
        ],
        faq: [
            {q: 'What is the theme wall?', a: 'A jungle-themed party wall with leaves and animal cutouts.'},
            {q: 'What are the three looks?', a: 'Pastel, with peach, mint and lilac; bright, with red, yellow, blue and green; and boho, with terracotta, sage and white.'},
            {q: 'Does the number balloon show a number?', a: 'Yes, a giant balloon showing the number one.'},
            {q: 'Can I ask for a car or princess theme?', a: 'Yes. Type a twist such as car theme or princess theme, and the same three pieces are shown in it.'},
        ],
    },
}
